import { useParams } from "react-router-dom";
import { Icon } from "@/lib/icons";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useApp } from "@/hooks/use-app-context";
import { useValidationGate } from "../useValidationGate";

/**
 * Full page shown instead of a contribution task while the account is
 * blocked by the validation gate. Same content and links as before, drawn
 * like the task pages (one title size, bordered cards, 44px targets).
 */
export function ValidationGateBlockedScreen() {
  const { t } = useApp();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || "fr";
  const gate = useValidationGate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-10 sm:px-6">
        <header className="space-y-3">
          <p className="text-xs font-medium uppercase tracking-[0.08em] text-brand">{t.validationGateBlockedEyebrow}</p>
          <h1 className="flex items-center gap-3 text-2xl font-semibold tracking-tight text-foreground">
            <span className="grid size-10 shrink-0 place-items-center rounded-full bg-brand/10 text-brand">
              <Icon name="lock" className="size-5" />
            </span>
            {t.validationGateBlockedTitle}
          </h1>
          <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
            {t.validationGateBlockedDesc
              .replace("{threshold}", String(gate.blockThreshold))
              .replace("{required}", String(gate.validationsNeeded))}
          </p>
        </header>

        <section className="space-y-2 rounded-xl border border-border/60 bg-surface p-5">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-foreground">
              {t.validationGateProgress
                .replace("{done}", String(gate.validationsCompleted))
                .replace("{total}", String(gate.validationsNeeded))}
            </span>
            <span className="tabular-nums text-muted-foreground">{gate.progressPercent}%</span>
          </div>
          <Progress value={gate.progressPercent} />
        </section>

        <section className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <Button asChild className="h-11">
            <a href={`/${currentLang}/validate-audio`}>
              <Icon name="checkCircle" />
              {t.validationGateGoValidateAudio}
            </a>
          </Button>
          <Button asChild variant="outline" className="h-11">
            <a href={`/${currentLang}/validate-transcription`}>
              <Icon name="checkCircle" />
              {t.validationGateGoValidateTranscription}
            </a>
          </Button>
          <a
            href={`/${currentLang}`}
            className="inline-flex h-11 items-center justify-center px-3 text-sm text-muted-foreground hover:text-foreground"
          >
            {t.validationGateBackToContribute}
          </a>
        </section>

        <section className="flex items-start gap-3 rounded-lg border border-border/60 bg-surface-2 px-4 py-3">
          <Icon name="users" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="text-sm">
            <p className="font-medium text-foreground">{t.validationGateWhyTitle}</p>
            <p className="mt-1 leading-6 text-muted-foreground">{t.validationGateWhyDesc}</p>
          </div>
        </section>
      </div>
    </div>
  );
}
